import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpParams } from '@angular/common/http';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { Mandant } from '../../../../../projects/kiene-core/src/lib/components/mandanten/classes/mandant';
import { Material } from '../../../warehouse/material/material';
import { SaveForAllDialogComponent } from '../../../dialogs/save-for-all-dialog/save-for-all-dialog.component';

export interface NebenkostenArtikelPickerRow {
    artikel_id: number;
    bezeichnung?: string;
    kuerzel?: string;
    buchungskonto?: string;
    warengruppe?: string;
}

@Component({
    selector: 'kiene-nebenkosten-artikel-picker-dialog',
    templateUrl: './nebenkosten-artikel-picker-dialog.component.html',
    styleUrls: ['./nebenkosten-artikel-picker-dialog.component.scss'],
})
export class NebenkostenArtikelPickerDialogComponent implements OnInit, OnDestroy {
    searchCtrl = new UntypedFormControl('');
    results: NebenkostenArtikelPickerRow[] = [];
    loading = false;
    showCreate = false;
    creating = false;
    /** Anlegen nur mit Artikel-Recht 1001 (Nebenkosten und Honorare). */
    canCreate = false;

    bezeichnungCtrl = new UntypedFormControl('', [Validators.required]);
    kuerzelCtrl = new UntypedFormControl('');
    buchungskontoCtrl = new UntypedFormControl('', [Validators.required]);
    buchungsgegenkontoCtrl = new UntypedFormControl('1600');
    buchungskontoOptions: { konto_nr: string; bezeichnung: string }[] = [];
    gegenkontoOptions: { konto_nr: string; bezeichnung: string }[] = [];

    private kuerzelTouched = false;
    private searchSub?: Subscription;
    private kontoSubs: Subscription[] = [];
    private readonly search$ = new Subject<string>();

    constructor(
        public dialogRef: MatDialogRef<NebenkostenArtikelPickerDialogComponent, Material | null>,
        @Inject(MAT_DIALOG_DATA) public data: { initialSearch?: string; honorar?: boolean },
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneBackendApiService,
        private readonly messageService: MessageService,
        private readonly localStorage: LocalStorageService,
        private readonly dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.canCreate = this.localStorage.currentUserHasPermission(1001);
        this.searchSub = this.search$
            .pipe(
                debounceTime(250),
                distinctUntilChanged(),
                switchMap(q => {
                    const term = (q || '').trim();
                    if (term.length < 1) {
                        this.results = [];
                        this.loading = false;
                        return of(null);
                    }
                    this.loading = true;
                    if (this.data?.honorar) {
                        const params = new HttpParams()
                            .set('search', term)
                            .set('honorar', '1')
                            .set('limit', '40')
                            .set('offset', '0');
                        return this.api.get(this.apiBaseUrl + 'artikel_autocomplete/', params).pipe(
                            catchError(err => {
                                this.loading = false;
                                this.results = [];
                                if (err?.status !== 0) {
                                    this.messageService.errorMessage('Suche fehlgeschlagen.', err);
                                }
                                return of(null);
                            }),
                        );
                    }
                    const params = new HttpParams()
                        .set('search', term)
                        .set('limit', '40')
                        .set('offset', '0');
                    return this.api.get(this.apiBaseUrl + 'fibu_nebenkosten_artikel/read.php', params).pipe(
                        catchError(err => {
                            this.loading = false;
                            this.results = [];
                            if (err?.status !== 0) {
                                this.messageService.errorMessage('Suche fehlgeschlagen.', err);
                            }
                            return of(null);
                        }),
                    );
                }),
            )
            .subscribe({
                next: res => {
                    this.loading = false;
                    if (!res) {
                        return;
                    }
                    const records = Array.isArray(res?.records) ? res.records : Array.isArray(res) ? res : [];
                    this.results = (records as any[]).map(r => ({
                        artikel_id: Number(r.artikel_id),
                        bezeichnung: r.bezeichnung || r.artikel_me || '',
                        kuerzel: r.kuerzel,
                        buchungskonto: r.buchungskonto,
                        warengruppe: r.warengruppe,
                    }));
                },
            });

        this.searchCtrl.valueChanges.subscribe((v: string) => this.search$.next(v || ''));

        this.bezeichnungCtrl.valueChanges.subscribe((v: string) => {
            if (this.kuerzelTouched) {
                return;
            }
            const bez = (v || '').trim();
            if (!bez) {
                this.kuerzelCtrl.setValue('', { emitEvent: false });
                return;
            }
            const first = bez.split(/\s+/)[0] || bez;
            this.kuerzelCtrl.setValue(first.substring(0, 12), { emitEvent: false });
        });
        this.kuerzelCtrl.valueChanges.subscribe(() => {
            this.kuerzelTouched = true;
        });

        this.wireSachkontoSearch(this.buchungskontoCtrl, opts => (this.buchungskontoOptions = opts));
        this.wireSachkontoSearch(this.buchungsgegenkontoCtrl, opts => (this.gegenkontoOptions = opts));

        const initial = (this.data?.initialSearch || '').trim();
        if (initial) {
            this.searchCtrl.setValue(initial);
        }
    }

    ngOnDestroy(): void {
        this.searchSub?.unsubscribe();
        this.kontoSubs.forEach(s => s.unsubscribe());
    }

    private wireSachkontoSearch(
        ctrl: UntypedFormControl,
        assign: (opts: { konto_nr: string; bezeichnung: string }[]) => void,
    ): void {
        const sub = ctrl.valueChanges
            .pipe(
                debounceTime(220),
                distinctUntilChanged(),
                switchMap(raw => {
                    const term = String(raw || '').trim();
                    if (term.length < 1) {
                        assign([]);
                        return of(null);
                    }
                    const params = new HttpParams()
                        .set('search', term)
                        .set('aktiv', '1')
                        .set('limit', '40')
                        .set('offset', '0');
                    return this.api.get(this.apiBaseUrl + 'fibu_konto/read.php', params).pipe(
                        catchError(() => {
                            assign([]);
                            return of(null);
                        }),
                    );
                }),
            )
            .subscribe(res => {
                if (!res) {
                    return;
                }
                const records = Array.isArray(res?.records) ? res.records : [];
                assign(
                    records
                        .filter((r: any) => {
                            const art = String(r.konto_art || 'SACHKONTO').toUpperCase();
                            return art === 'SACHKONTO' || art === '';
                        })
                        .map((r: any) => ({
                            konto_nr: String(r.konto_nr || ''),
                            bezeichnung: String(r.bezeichnung || ''),
                        })),
                );
            });
        this.kontoSubs.push(sub);
    }

    select(row: NebenkostenArtikelPickerRow): void {
        this.dialogRef.close(this.toMaterial(row));
    }

    toggleCreate(): void {
        if (!this.canCreate) {
            this.messageService.infoMessage('Kein Recht zum Anlegen von Artikeln (1001).');
            return;
        }
        this.showCreate = !this.showCreate;
        if (this.showCreate && !this.bezeichnungCtrl.value) {
            const q = (this.searchCtrl.value || '').trim();
            if (q && !/^\d+(\.\d+)?$/.test(q)) {
                this.bezeichnungCtrl.setValue(q);
            } else if (q && /^\d+(\.\d+)?$/.test(q)) {
                this.buchungskontoCtrl.setValue(q);
            }
        }
    }

    isCreateValid(): boolean {
        return this.bezeichnungCtrl.valid && this.buchungskontoCtrl.valid;
    }

    create(): void {
        if (!this.canCreate || !this.isCreateValid() || this.creating) {
            return;
        }
        const body = this.buildCreateBody();
        this.creating = true;
        this.api.get(this.apiBaseUrl + 'fibu_nebenkosten_artikel/read_mandanten.php').subscribe({
            next: (response: any) => {
                const records = response?.records ?? [];
                const available = this.toMandanten(records);
                if (available.length <= 1) {
                    this.postCreate(body);
                    return;
                }
                this.creating = false;
                const dialogRef = this.dialog.open(SaveForAllDialogComponent, {
                    width: '520px',
                    data: {
                        availableMandanten: available,
                        buttonText: 'Anlegen',
                    },
                });
                dialogRef.afterClosed().subscribe((result: any) => {
                    if (!result) {
                        return;
                    }
                    const ids = Array.isArray(result) ? result : result.mandanten_ids || [];
                    if (!ids.length) {
                        return;
                    }
                    body.mandanten_ids = ids;
                    this.creating = true;
                    this.postCreate(body);
                });
            },
            error: err => {
                // Fallback: nur aktueller Mandant
                this.postCreate(body);
                if (err?.status && err.status !== 0) {
                    // still create; optional soft warn
                }
            },
        });
    }

    close(): void {
        this.dialogRef.close(null);
    }

    private buildCreateBody(): any {
        return {
            bezeichnung: (this.bezeichnungCtrl.value || '').trim(),
            kuerzel: (this.kuerzelCtrl.value || '').trim() || null,
            buchungskonto: (this.buchungskontoCtrl.value || '').trim(),
            buchungsgegenkonto: (this.buchungsgegenkontoCtrl.value || '').trim() || '1600',
            einheit_id: 9,
            ust: 19,
            honorar: this.data?.honorar ? 1 : 0,
        };
    }

    private postCreate(body: any): void {
        this.api.post(this.apiBaseUrl + 'fibu_nebenkosten_artikel/create.php', body).subscribe({
            next: (res: NebenkostenArtikelPickerRow | { records?: NebenkostenArtikelPickerRow[] }) => {
                this.creating = false;
                const created =
                    res && (res as NebenkostenArtikelPickerRow).artikel_id
                        ? (res as NebenkostenArtikelPickerRow)
                        : Array.isArray((res as any)?.records)
                          ? (res as any).records[0]
                          : null;
                if (!created?.artikel_id) {
                    this.messageService.infoMessage('Anlegen ohne gültige Antwort.');
                    return;
                }
                const label = this.data?.honorar ? 'Honorar-Artikel' : 'Nebenkosten-Artikel';
                this.messageService.infoMessage(label + ' angelegt.');
                this.dialogRef.close(this.toMaterial(created));
            },
            error: err => {
                this.creating = false;
                this.messageService.errorMessage('Anlegen fehlgeschlagen.', err);
            },
        });
    }

    private toMandanten(records: any[]): Mandant[] {
        return (records || [])
            .map(r => {
                const m = new Mandant();
                m.mandant_id = Number(r.mandant_id);
                m.bezeichnung = r.bezeichnung || '';
                return m;
            })
            .filter(m => m.mandant_id > 0);
    }

    private toMaterial(row: NebenkostenArtikelPickerRow): Material {
        const m = new Material();
        m.artikel_id = row?.artikel_id;
        m.bezeichnung = row?.bezeichnung || (row as any)?.artikel_me || '';
        m.kuerzel = row?.kuerzel || '';
        m.artikel_me = row?.bezeichnung || (row as any)?.artikel_me || row?.kuerzel || '';
        (m as any).buchungskonto = row?.buchungskonto || null;
        return m;
    }
}
